// Call this as an import, then access it by creating a new instance
// ie ---- const cookies = new Cookies()
// and then access all the methods, objects, etc via that object
import type {TransactionDetails} from '../types/StockhornTypes';
import {getAmountValue} from '../components/sortUtils';
interface CookieParams {
  [key: string]: any; // Flexible params like expires, path, etc.
}

export class Cookies {
  private initialized: boolean = false;
  private lsObject: boolean = true; // TRUE tells us to store/read the LocalStorage value as a JSON object, FALSE is individual name-value pairs
  private cookieTime: number = 30; // 30 days expiration in case we have to use cookie storage and the expiration string is not passed in
  private storageKEY: string = "TableAppData"; // The name of the LocalStorage object to store all our name-value pairs in
  private isLocalStorageEnabled: boolean = false; // Initialized in checkStorageAvailability

  private init(): void {
    this.checkStorageAvailability(); // Run this to find out if Local Storage is available - should be
    this.initialized = true;
  }

  public set(name: string, value: any, params?: CookieParams): void { // mimics import { Cookies } from "react-cookie" so we don't have to rewrite our calls
    if (!this.initialized) {
      this.init();
    }
    if (this.lsObject) {
      this.setNameValuePairAsJSON(name, value, params);
    } else {
      this.setSingleNameValuePair(name, value, params);
    }
  }

    public setWithReducer(name: string, value: any, metaData:any, params?: CookieParams): void { // mimics import { Cookies } from "react-cookie" so we don't have to rewrite our calls
    if (!this.initialized) {
      this.init();
    }
    if (this.lsObject) {
      // let sum = 0;
      // Object.entries(value).forEach(([key, data]) => {
      //     const transaction = data as TransactionDetails;
      //     sum += parseFloat(transaction.Amount);
      // });
      const sum = Object.entries(value).reduce((accumulator, [key, data]) => {
          const transaction = data as TransactionDetails;
          return accumulator + Math.abs(getAmountValue(transaction));
      }, 0);
      this.setNameValuePairAsJSON(name, {rows:value,amount:sum.toFixed(2), ...metaData}, params);
    } else {
      this.setSingleNameValuePair(name, value, params);
    }
  }



  public get(name: string): any { // mimics import { Cookies } from "react-cookie" so we don't have to rewrite our calls
    if (!this.initialized) {
      this.init();
    }
    if (this.lsObject) {
      return this.getNameValuePairFromJSON(name);
    } else {
      return this.getSingleNameValuePair(name);
    }
  }

    public getAll(): any { // mimics import { Cookies } from "react-cookie" so we don't have to rewrite our calls
    if (!this.initialized) {
      this.init();
    }
    if (this.lsObject) {
      return this.parseLocalStorageJSON();
    } else {
      return this.parseLocalStorageJSON();
    }
  }

  private getBaseDomain(): string | null { // For cookie storage, use the base domain so we don't have to worry about sub-domain issues
    let sld: string = document.domain;
    const dp: string[] = sld.split(".");
    const len: number = dp.length;
    if (len < 2) {
      sld = "";
    } else if (!isNaN(parseInt(dp[len - 1], 10)) && !isNaN(parseInt(dp[len - 2], 10))) {
      sld = "";
    } else {
      sld = "." + dp[len - 2] + "." + dp[len - 1];
    }
    return sld || null;
  }

  private setCookie(name: string, value: string, domain: string | null, expirationTime: number, params?: CookieParams): void { // If we use cookies, we either set based on my defaults of 30 days in the path "/",
    // or use the values passed in via the "params" object
    const de: Date = new Date();
    de.setTime(de.getTime() + expirationTime * 1000);
    const expTime: string = (params && params.expires ? params.expires : de.toUTCString());
    const path: string = (params && params.path ? params.path : "/");
    document.cookie = name + "=" + escape(value) + "; expires=" + expTime + "; path=" + path + ((domain == null) ? "" : (";domain=" + domain));
  }

  private getCookie(name: string): string {
    if (!name) {
      return "";
    }
    return decodeURIComponent(document.cookie.replace(new RegExp("(?:(?:^|.*;)\\s*" + encodeURIComponent(name)
      .replace(/[-.+*]/g, "\\$&") + "\\s*\\=\\s*([^;]*).*$)|^.*$"), "$1")) || "";
      //.replace(/[\-\.\+\*]/g, "\\$&") + "\\s*\\=\\s*([^;]*).*$)|^.*$"), "$1")) || "";
  }

  private setSingleNameValuePair(name: string, val: any, params?: CookieParams): void { // We are setting a single localStorage/cookie value for the name-value pair
    if (this.isLocalStorageEnabled) {
      localStorage.setItem(name, val as string); // Assuming val is stringifiable
    } else {
      if (!!document.cookie) {
        this.setCookie(name, val as string, this.getBaseDomain(), this.cookieTime * 24 * 60 * 60, params);
      } else {
        return;
      }
    }
  }

  private filterKey<T, K extends keyof T>(obj: T, keyToRemove: K): Omit<T, K> {
  const { [keyToRemove]: _, ...rest } = obj;
  return rest;
}

  public removeSingleNameValuePair(name: string): void { // We are setting a single localStorage/cookie value for the name-value pair
    if (!this.initialized) {
      this.init();
    }
    const lsVal = this.parseLocalStorageJSON();
    if (this.isLocalStorageEnabled) {
      const result = this.filterKey(lsVal, name);
      localStorage.setItem(this.storageKEY, JSON.stringify(result));
    } else {
      return;
    }
  }

  private getSingleNameValuePair(name: string): string | null { // We are reading a single localStorage/cookie value for the name-value pair
    if (this.isLocalStorageEnabled) {
      return localStorage.getItem(name);
    } else {
      if (!!document.cookie) {
        return this.getCookie(name);
      } else {
        return "";
      }
    }
  }

  private setNameValuePairAsJSON(name: string, value: any, params?: CookieParams): void { // We are setting the name-value pair as a localStorage property within an object named by the "storageKEY" value
    let lsVal = this.parseLocalStorageJSON();
    let found: boolean = false;
    let tmp: { value: any, [key: string]: any } = { value: "" };
    params = params || {};
    for (let n in lsVal) {
      if (n === name) {
        found = true;
        tmp.value = value;
        for (let m in params) {
          tmp[m] = params[m];
        }
        lsVal[n] = tmp;
      }
    }
    if (!found) {
      lsVal[name] = {value};
      lsVal[name].value = value;
      for (let m in params) {
        lsVal[name][m] = params[m];
      }
    }
    localStorage.setItem(this.storageKEY, JSON.stringify(lsVal));
  }

  private getNameValuePairFromJSON(name: string): any { // We are getting the name-value pair from a localStorage property within an object named by the "storageKEY" value,
    // unless it does not exist, in which case we look for it in cookies
    let lsVal = this.parseLocalStorageJSON();
    let found: boolean = false;
    for (let n in lsVal) {
      if (n === name) {
        found = true;
        return lsVal[n].value;
      }
    }
    if (!found) { // if not found in localStorage, look for in cookies
      let tryInCookies: string = this.getCookie(name);
      return tryInCookies;
    }
  }

  private parseLocalStorageJSON(): Record<string, { value: any, [key: string]: any }> {
    const lsVal: string | null = localStorage.getItem(this.storageKEY);
    if (!lsVal) {
      return {};
    } else {
      return JSON.parse(lsVal);
    }
  }

  private checkStorageAvailability(): void {
    const testKey: string = "test";
    try {
      localStorage.setItem(testKey, "1");
      localStorage.getItem(testKey);
      this.isLocalStorageEnabled = true;
      localStorage.removeItem(testKey);
    } catch (error) {
      this.isLocalStorageEnabled = false;
    }
  }
}

export default Cookies;